import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, Calculator } from "lucide-react";

const HERO_IMG = "https://images.unsplash.com/photo-1640357897497-599b4fc84f51?crop=entropy&cs=srgb&fm=jpg&q=85";

const lines = [
  { text: "Websites engineered", gold: false },
  { text: "for the trades that", gold: false },
  { text: "build America.", gold: true },
];

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "25%"]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  const go = (href) => document.querySelector(href)?.scrollIntoView({ behavior: "smooth" });

  return (
    <section ref={ref} data-testid="agency-hero-section" className="relative min-h-screen flex items-center overflow-hidden">
      <motion.div style={{ y: bgY }} className="absolute inset-0 -z-10">
        <img src={HERO_IMG} alt="" className="w-full h-[130%] object-cover opacity-25" />
        <div className="absolute inset-0 bg-gradient-to-b from-obsidian/70 via-obsidian/60 to-obsidian" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_60%_50%_at_70%_20%,rgba(212,175,55,0.12),transparent)]" />
      </motion.div>

      <motion.div style={{ opacity: fade }} className="max-w-7xl mx-auto px-6 lg:px-10 pt-36 pb-24 w-full">
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.2 }}
          className="font-mono text-xs uppercase tracking-[0.35em] text-gold/80 mb-8"
          data-testid="hero-eyebrow"
        >
          San Diego · Web Design for the Trades
        </motion.p>

        <h1 className="font-serif font-light text-5xl sm:text-6xl lg:text-8xl leading-[1.05] tracking-tight text-[#F8F6F0] max-w-5xl" data-testid="hero-headline">
          {lines.map((line, i) => (
            <span key={i} className="hero-line-mask">
              <motion.span
                className={`block ${line.gold ? "gold-text italic font-medium" : ""}`}
                initial={{ y: "110%" }}
                animate={{ y: 0 }}
                transition={{ duration: 1, delay: 0.35 + i * 0.18, ease: [0.16, 1, 0.3, 1] }}
              >
                {line.text}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 1.1 }}
          className="mt-8 max-w-xl text-base sm:text-lg text-zinc-400 leading-relaxed"
          data-testid="hero-subcopy"
        >
          Beautiful contractor websites with clear pricing and real interactive examples.
          Choose your trade, then explore Starter, Growth and Premium designs.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 1.3 }}
          className="mt-12 flex flex-wrap items-center gap-5"
        >
          <button
            data-testid="hero-view-portfolio-btn"
            onClick={() => go("#work")}
            className="group flex items-center gap-3 px-8 py-4 rounded-full bg-gold text-obsidian font-sans font-semibold text-sm tracking-wide hover:bg-gold-hover transition-all duration-300 hover:shadow-[0_0_40px_rgba(212,175,55,0.35)]"
          >
            View Portfolio
            <ArrowRight size={17} className="group-hover:translate-x-1 transition-transform duration-300" />
          </button>
          <button
            data-testid="hero-roi-calc-btn"
            onClick={() => go("#roi")}
            className="group flex items-center gap-3 px-8 py-4 rounded-full border border-zinc-700 text-zinc-300 font-sans text-sm tracking-wide hover:border-gold/50 hover:text-gold transition-all duration-300"
          >
            <Calculator size={16} />
            Calculate Website ROI
          </button>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 1.7 }}
          className="mt-20 grid grid-cols-3 max-w-lg gap-6"
          data-testid="hero-stats"
        >
          {[
            ["40+", "Sites Launched"],
            ["210%", "Avg. Lead Lift"],
            ["14 Days", "Avg. Build Time"],
          ].map(([num, label]) => (
            <div key={label}>
              <div className="font-serif text-3xl sm:text-4xl gold-text">{num}</div>
              <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500 mt-2">{label}</div>
            </div>
          ))}
        </motion.div>
      </motion.div>
    </section>
  );
}
